import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { PageHeader, StatCard, Empty, Badge } from "@/components/common";
import { STATUSES } from "@/lib/status";
import { Database, Briefcase, Users, UsersRound, Loader2 } from "lucide-react";

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [s, p] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/positions"),
        ]);
        setStats(s.data);
        setPositions(p.data);
      } catch (e) {
        setError(formatErr(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return (
    <div className="p-8 flex items-center gap-2 text-gray-500"><Loader2 className="animate-spin" size={18} /> Loading overview...</div>
  );

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <PageHeader
        testId="admin-overview-header"
        title="Control Room"
        subtitle="A live snapshot of recruitment operations across Neoventra Inc."
      />

      {error && (
        <div className="mb-6 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="stats-grid">
        <Link to="/leads" className="block">
          <StatCard testId="stat-leads" label="Total Leads" value={stats?.total_leads ?? 0} icon={Database} />
        </Link>
        <Link to="/positions" className="block">
          <StatCard testId="stat-open-positions" label="Open Positions" value={stats?.open_positions ?? 0} icon={Briefcase} />
        </Link>
        <Link to="/positions" className="block">
          <StatCard testId="stat-total-positions" label="Total Positions" value={stats?.total_positions ?? 0} icon={Briefcase} />
        </Link>
        <Link to="/candidates" className="block">
          <StatCard testId="stat-candidates" label="Candidates" value={stats?.total_candidates ?? 0} icon={UsersRound} />
        </Link>
      </div>

      {/* Candidate Pipeline */}
      <div className="card-base mb-8">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold tracking-tight">Candidate Pipeline</h3>
          <Link to="/candidates" className="text-xs text-brand hover:underline" data-testid="pipeline-view-all">View all →</Link>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 divide-x divide-gray-100" data-testid="pipeline-strip">
          {STATUSES.map((s) => (
            <Link
              key={s.value}
              to="/candidates"
              className="p-4 hover:bg-gray-50 transition-colors text-center"
              data-testid={`pipeline-strip-${s.value}`}
            >
              <div className={`inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${s.color}`}>
                {s.label}
              </div>
              <div className="font-mono text-2xl font-bold text-gray-900 mt-2">
                {stats?.pipeline_counts?.[s.value] ?? 0}
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recruiter Activity */}
        <div className="card-base">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-heading text-lg font-bold tracking-tight">Recruiter Activity</h3>
            <Users size={16} className="text-gray-400" />
          </div>
          <div className="overflow-x-auto" data-testid="recruiter-activity-table">
            {(stats?.recruiter_activity || []).length === 0 ? (
              <div className="p-6">
                <Empty title="No submissions yet" subtitle="Recruiter submissions will appear here." />
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500 py-3 px-4">Recruiter</th>
                    <th className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500 py-3 px-4">Position</th>
                    <th className="text-right text-xs font-semibold uppercase tracking-wider text-gray-500 py-3 px-4">Submissions</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recruiter_activity.map((r, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-900">{r.recruiter_name || r.recruiter_email}</div>
                        <div className="text-xs text-gray-500">{r.recruiter_email}</div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-gray-900">{r.position_name}</div>
                        <div className="text-xs text-gray-500">{r.client_company}</div>
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-bold">{r.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Open Positions */}
        <div className="card-base">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-heading text-lg font-bold tracking-tight">Open Positions</h3>
            <Briefcase size={16} className="text-gray-400" />
          </div>
          <div className="overflow-x-auto" data-testid="open-positions-overview">
            {positions.filter(p => p.status === "open").length === 0 ? (
              <div className="p-6"><Empty title="No open positions" /></div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500 py-3 px-4">Client / Position</th>
                    <th className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500 py-3 px-4">Location</th>
                    <th className="text-right text-xs font-semibold uppercase tracking-wider text-gray-500 py-3 px-4">Candidates</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.filter(p => p.status === "open").slice(0, 8).map((p) => (
                    <tr key={p.id} className="border-t border-gray-100">
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-900">{p.position_name}</div>
                        <div className="text-xs text-gray-500">{p.client_company}</div>
                      </td>
                      <td className="py-3 px-4 text-gray-700">{p.location || "—"}</td>
                      <td className="py-3 px-4 text-right">
                        <Badge variant="brand">{p.candidate_count}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
